import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function Register({ onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [verifyCode, setVerifyCode] = useState("");
  const [step, setStep] = useState("register"); // "register" ou "verify"
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true); // Pour animation de fermeture
  const [showPassword, setShowPassword] = useState(false); // Pour afficher/masquer le mot de passe

  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
      navigate("/"); // retour vers accueil (optionnel)
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        formData,
        { withCredentials: true }
      );

      setSuccess(response.data.message || "Inscription réussie !");
      setStep("verify");
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Erreur réseau ou serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/verify`,
        {
          email: formData.email,
          code: verifyCode,
        },
        { withCredentials: true }
      );

      setSuccess("Email vérifié avec succès !");
      setTimeout(() => {
        if (onClose) onClose();
        navigate("/login");
      }, 1500);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Erreur réseau ou serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          style={styles.wrapper}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            style={styles.modal}
          >
            <button
              style={styles.closeBtn}
              onClick={handleClose}
              aria-label="Fermer la fenêtre"
            >
              <FaTimes />
            </button>

            <h2 style={styles.title}>
              {step === "register" ? "S'inscrire" : "Vérification"}
            </h2>

            {step === "register" ? (
              <form onSubmit={handleSubmit}>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Nom *"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputWrapper}>
                  <input
                    type="email"
                    name="email"
                    placeholder="E-mail *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputWrapper}>
                  <div style={styles.passwordContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Mot de passe *"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      style={styles.passwordInput}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={styles.eyeButton}
                      aria-label={
                        showPassword
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={styles.submitBtn}
                >
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: "2px solid #fff",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      Chargement...
                    </div>
                  ) : (
                    "S'inscrire"
                  )}
                </motion.button>
              </form>
            ) : (
              <form onSubmit={handleVerify}>
                <p style={{ marginBottom: "20px", fontSize: "15px" }}>
                  Un code vous a été envoyé par e-mail. Entrez-le ci-dessous
                  pour activer votre compte :
                </p>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    placeholder="Code de vérification *"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    required
                    style={styles.input}
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={styles.submitBtn}
                >
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: "2px solid #fff",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      Vérification...
                    </div>
                  ) : (
                    "Vérifier le code"
                  )}
                </motion.button>
              </form>
            )}

            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}

            {step === "register" && (
              <div style={styles.footer}>
                <span>Vous avez déjà un compte ? </span>
                <span
                  style={styles.link}
                  onClick={() => {
                    navigate("/login");
                    if (onClose) onClose();
                  }}
                >
                  Connectez-vous ici
                </span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const styles = {
  wrapper: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(10px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    fontFamily: "'Cormorant Garamond', serif",
  },
  modal: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    padding: "50px 40px",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "600px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    position: "relative",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtn: {
    position: "absolute",
    top: "15px",
    right: "15px",
    fontSize: "22px",
    color: "#2c1810",
    cursor: "pointer",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    borderRadius: "50%",
    padding: "8px",
    transition: "all 0.3s ease",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "30px",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
    letterSpacing: "1px",
  },
  inputWrapper: {
    position: "relative",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    alignItems: "center",
  },
  input: {
    width: "100%",
    padding: "8px 15px",
    borderRadius: "20px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    fontSize: "14px",
    transition: "all 0.3s ease",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    width: "100%",
    padding: "8px 15px",
    paddingRight: "45px", // Espace pour le bouton œil
    borderRadius: "20px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    fontSize: "14px",
    transition: "all 0.3s ease",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  eyeButton: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    cursor: "pointer",
    padding: "6px",
    color: "#5a4a3a",
    fontSize: "14px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "20px",
    width: "28px",
    height: "28px",
  },
  submitBtn: {
    width: "100%",
    maxWidth: "400px",
    padding: "8px 15px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "'Cormorant Garamond', serif",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    marginTop: "20px",
  },
  error: {
    color: "#d32f2f",
    marginTop: "15px",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "16px",
  },
  success: {
    color: "#4caf50",
    marginTop: "15px",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "16px",
  },
  footer: {
    marginTop: "30px",
    textAlign: "center",
    borderTop: "1px solid rgba(212, 175, 55, 0.2)",
    paddingTop: "20px",
    fontSize: "16px",
    fontFamily: "'Cormorant Garamond', serif",
    color: "#5a4a3a",
  },
  link: {
    color: "#d4af37",
    fontWeight: "600",
    cursor: "pointer",
    marginLeft: "5px",
    textDecoration: "underline",
    fontFamily: "'Cormorant Garamond', serif",
    transition: "all 0.3s ease",
  },
};
